// filepath: src/components/Console.jsx
import React, { useState, useMemo, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CssBaseline,
  Box,
  Button,
  Divider,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link2 } from "lucide-react";
import Home from "./Home";
import About from "./About";
import Features from "./Features";
import Contact from "./Contact";
import Dashboard from "./DashBoard";
import LanguageSwitcher from "./LanguageSwitcher";

const drawerWidth = 240;

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -4 },
};

const pageTransition = {
  type: "tween",
  ease: "easeOut",
  duration: 0.28,
};

const Console = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!location.hash) return undefined;

    const timeout = window.setTimeout(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    }, 320);

    return () => window.clearTimeout(timeout);
  }, [location.pathname, location.hash]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "dark",
          primary: { main: "#70cbb0" },
          background: {
            default: "#090b10",
            paper: "#11151c",
          },
        },
        typography: { fontFamily: "'Inter', 'Roboto', sans-serif" },
        shape: { borderRadius: 12 },
      }),
    []
  );

  const menuItems = [
    { text: t("home1"), path: "/", key: "home" },
    { text: t("about1"), path: "/about", key: "about" },
    { text: t("features1"), path: "/features", key: "features" },
    { text: t("contact1"), path: "/contact", key: "contact" },
    { text: t("dashboard1"), path: "/dashboard", key: "dashboard" },
  ];

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawer = (
    <Box className="app-drawer" sx={{ width: drawerWidth, height: "100%" }} role="presentation">
      <Toolbar />
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem>
          <LanguageSwitcher />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="app-console" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <AppBar position="sticky" className="app-header">
          <Toolbar className="app-toolbar">
            <IconButton
              color="inherit"
              aria-label="menu"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1.5, display: { md: "none" }, color: "#c8d0d8" }}
            >
              <MenuIcon />
            </IconButton>
            <Link className="app-brand" to="/" aria-label="Linkpin home">
              <span className="app-brand__mark"><Link2 size={18} aria-hidden="true" /></span>
              Linkpin
            </Link>
            <Box sx={{ flexGrow: 1 }} />
            <Box className="app-nav" sx={{ display: { xs: "none", md: "flex" } }}>
              {menuItems.map((item) => (
                <Button
                  key={item.key}
                  component={Link}
                  to={item.path}
                  className={`app-nav-link${location.pathname === item.path ? " app-nav-link--active" : ""}`}
                >
                  {item.text}
                </Button>
              ))}
              <LanguageSwitcher />
            </Box>
            <Button
              component={Link}
              to="/#link-studio"
              className="app-header-cta"
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            >
              Shorten a link
            </Button>
          </Toolbar>
        </AppBar>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: drawerWidth, borderRight: "1px solid rgba(255,255,255,0.08)" },
          }}
        >
          {drawer}
        </Drawer>

        <Box component="main" className="app-main">
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
              <Route path="/about" element={<PageWrapper><About /></PageWrapper>} />
              <Route path="/features" element={<PageWrapper><Features /></PageWrapper>} />
              <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
              <Route path="/dashboard" element={<PageWrapper><Dashboard /></PageWrapper>} />
            </Routes>
          </AnimatePresence>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

const PageWrapper = ({ children }) => (
  <motion.div
    className="app-page-transition"
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
  >
    {children}
  </motion.div>
);

export default Console;
